import { countHotelNights } from "@/lib/daycare-hotel/stayBilling";
import type { ComandaLine, ReservationFormValues } from "./reservationForm";
import type { Database } from "@/integrations/supabase/types";

type StayReservationInsert = Database["public"]["Tables"]["stay_reservations"]["Insert"];

export function buildReservationPayload(values: ReservationFormValues, clinicId: string): StayReservationInsert {
  // `arrival_shift`, `departure_shift` e `billing_timing` são colunas novas: o
  // `types.ts` é gerado e só as conhece depois que o Rafael regenera.
  return {
    clinic_id: clinicId,
    pet_id: values.petId,
    module_type: values.moduleType === "day_care" ? "daycare" : "hotel",
    is_overnight: values.moduleType === "hotel" || values.isOvernight,
    check_in_date: values.checkInDate,
    check_out_date: values.checkOutDate ?? null,
    check_in_time: null,
    check_out_time: null,
    hotel_room_id: values.hotelRoomId ?? null,
    hotel_room_bed_id: values.hotelRoomBedId ?? null,
    stay_pack_id: values.stayPackIds[0] ?? null,
    arrival_shift: values.arrivalShift ?? null,
    departure_shift: values.departureShift ?? null,
    billing_timing: values.billingTiming ?? null,
    status: "reserved",
  } as StayReservationInsert;
}

export function buildStayComandaLines(lines: ComandaLine[]) {
  return lines
    .filter((line) => Boolean(line?.itemName))
    .map((line) => ({
      itemName: String(line.itemName ?? ""),
      quantity: Number(line.quantity ?? 1),
      unitPrice: Number(line.unitPrice ?? 0),
      itemId: line.itemId ?? null,
    }));
}

export function getHotelReservationNights(values: Pick<ReservationFormValues, "moduleType" | "checkInDate" | "checkOutDate">) {
  return values.moduleType === "hotel" && values.checkOutDate ? countHotelNights(values.checkInDate, values.checkOutDate) : 0;
}

function eachNight(checkInDate: string, checkOutDate: string): string[] {
  const nights: string[] = [];
  for (let day = checkInDate; day < checkOutDate; ) {
    nights.push(day);
    const date = new Date(`${day}T12:00:00`);
    date.setDate(date.getDate() + 1);
    day = date.toLocaleDateString("en-CA");
  }
  return nights;
}

export interface StayPlanHold {
  usedOn: string;
  units: number;
}

/**
 * Unidades que a reserva segura. Creche: uma por ocorrência. Hotel: uma por
 * noite. Nada é debitado aqui — o débito é do check-in.
 */
export function buildStayPlanHolds({
  values,
  occurrences,
}: {
  values: Pick<ReservationFormValues, "moduleType" | "checkInDate" | "checkOutDate" | "planChoice">;
  occurrences: string[];
}): StayPlanHold[] {
  if (values.planChoice === "avulso") return [];

  const days = values.moduleType === "hotel"
    ? (values.checkOutDate ? eachNight(values.checkInDate, values.checkOutDate) : [values.checkInDate])
    : (occurrences.length > 0 ? occurrences : [values.checkInDate]);

  return days.map((usedOn) => ({ usedOn, units: 1 }));
}
